import type {
  BudgetSettings, VenueRoomBreakdown, VenueActivitiesBreakdown,
  VenueMealsBreakdown, VenueMeetingsBreakdown,
} from '@/services/BudgetService';

export interface BudgetRates {
  base: string;
  rates: Record<string, number>;
  timestamp?: number;
}

export interface BudgetBreakdown {
  roomBreakdown?: Partial<VenueRoomBreakdown> | null;
  activitiesBreakdown?: Partial<VenueActivitiesBreakdown> | null;
  mealsBreakdown?: Partial<VenueMealsBreakdown> | null;
  meetingsBreakdown?: Partial<VenueMeetingsBreakdown> | null;
  otherCosts?: {
    venue_currency?: string;
    flight_cost_per_attendee?: number | null;
    ground_transport_cost?: number | null;
    miscellaneous_costs?: Array<{ amount: number; type: string }> | null;
  } | null;
}

/** Missing FX must never silently relabel an unconverted amount as another currency. */
export function budgetFxRate(from: string, to: string, snapshot: BudgetRates | null): number {
  if (from === to) return 1;
  if (!snapshot) return NaN;
  const source = from === snapshot.base ? 1 : snapshot.rates[from];
  const target = to === snapshot.base ? 1 : snapshot.rates[to];
  if (!Number.isFinite(source) || source <= 0 || !Number.isFinite(target) || target <= 0) return NaN;
  return target / source;
}

export const BUDGET_FX_UNAVAILABLE = 'Exchange rates are unavailable or still loading. Converted budgets are hidden; refresh to retry.';

/**
 * Single calculation contract for editor summaries, venue editor, Destination,
 * Comparison and hotel-card prices. Input amounts are cents in venue currency.
 * Keep FX precision through multiplication; round only at the display boundary.
 * Fixed fees are cents in EVENT currency. Flights never enter any percent fee base.
 */
export function calculateProposalBudget({
  breakdown, settings, attendees, nights, eventCurrency, exchangeRates,
}: {
  breakdown: BudgetBreakdown;
  settings: Pick<BudgetSettings, 'contingency_percent' | 'contingency_fixed' |
    'planning_overhead_percent' | 'planning_overhead_fixed' | 'profit_margin_percent' | 'profit_margin_fixed'>;
  attendees: number;
  nights: number;
  eventCurrency: string;
  exchangeRates: BudgetRates | null;
}) {
  const venueCurrency = breakdown.otherCosts?.venue_currency || 'USD';
  const fx = budgetFxRate(venueCurrency, eventCurrency, exchangeRates);
  const rb = breakdown.roomBreakdown;
  const ab = breakdown.activitiesBreakdown;
  const mb = breakdown.mealsBreakdown;
  const meetings = breakdown.meetingsBreakdown;
  const other = breakdown.otherCosts;
  const roomNights = rb?.room_nights ?? nights;
  const singleRoomRate = (rb?.single_room_rate ?? 0) / 100 * fx;
  const doubleRoomRate = (rb?.double_room_rate ?? 0) / 100 * fx;

  // Sum each source-currency category before FX, without rounding converted unit rates.
  const accommodation = ((rb?.single_room_rate ?? 0) * (rb?.single_rooms ?? 0) +
    (rb?.double_room_rate ?? 0) * (rb?.double_rooms ?? 0)) * roomNights / 100 * fx;
  const activities = (ab?.activities_number ?? 0) * (ab?.activities_cost_per ?? 0) * attendees / 100 * fx;
  const meals = ((mb?.breakfast_cost_per_attendee ?? 0) * (mb?.breakfast_count ?? 0) +
    (mb?.lunch_cost_per_attendee ?? 0) * (mb?.lunch_count ?? 0) +
    (mb?.dinner_cost_per_attendee ?? 0) * (mb?.dinner_count ?? 0) +
    (mb?.alcohol_cost_per_attendee ?? 0) * (mb?.alcohol_count ?? 0)) * attendees / 100 * fx;
  const meetingTotal = (((meetings?.meeting_room_rental ?? 0) +
    (meetings?.meeting_setup_fees ?? 0) + (meetings?.meeting_av_cost ?? 0)) *
    (meetings?.number_of_meetings ?? 0) + (meetings?.coffee_cost_per_attendee ?? 0) *
    (meetings?.coffee_count ?? 0) * attendees) / 100 * fx;
  const flightCost = (other?.flight_cost_per_attendee ?? 0) * attendees / 100 * fx;
  const groundTransport = (other?.ground_transport_cost ?? 0) * attendees / 100 * fx;
  const miscellaneous = (other?.miscellaneous_costs ?? []).reduce((sum, item) =>
    sum + item.amount * (item.type === 'per_person' ? attendees : 1), 0) / 100 * fx;
  const subtotal = accommodation + activities + meals + meetingTotal + flightCost + groundTransport + miscellaneous;
  const nonFlightSubtotal = subtotal - flightCost;
  const contingencyAmount = settings.contingency_fixed != null
    ? settings.contingency_fixed / 100 : nonFlightSubtotal * settings.contingency_percent / 100;
  const feeBase = nonFlightSubtotal + contingencyAmount;
  const planningOverheadAmount = settings.planning_overhead_fixed != null
    ? settings.planning_overhead_fixed / 100 : feeBase * settings.planning_overhead_percent / 100;
  const profitMarginAmount = settings.profit_margin_fixed != null
    ? settings.profit_margin_fixed / 100 : feeBase * settings.profit_margin_percent / 100;
  const totalProgramCost = subtotal + contingencyAmount + planningOverheadAmount + profitMarginAmount;
  const perAttendeeCost = !Number.isFinite(totalProgramCost) ? NaN : attendees > 0 ? totalProgramCost / attendees : 0;
  return {
    available: Number.isFinite(totalProgramCost), venueCurrency, eventCurrency,
    accommodation, activities, meals, meetings: meetingTotal, flightCost, groundTransport,
    miscellaneous, subtotal, contingencyAmount, planningOverheadAmount, profitMarginAmount,
    totalProgramCost, perAttendeeCost, singleRoomRate, doubleRoomRate, roomNights,
  };
}

/** API response wrapper normalization is kept out of the calculation contract. */
export function normalizeBudgetRates(value: unknown): BudgetRates | null {
  if (!value || typeof value !== 'object') return null;
  const outer = value as Record<string, unknown>;
  const inner = outer.rates && typeof outer.rates === 'object' &&
    'rates' in outer.rates ? outer.rates as Record<string, unknown> : outer;
  const rates = inner.rates as Record<string, number> | undefined;
  if (!rates || typeof rates !== 'object') return null;
  return { base: typeof inner.base === 'string' ? inner.base : 'USD', rates,
    timestamp: typeof inner.timestamp === 'number' ? inner.timestamp : undefined };
}
